import { defineStore } from "pinia";
import { watch } from "vue";
import { resetAuthenticatedConsumer } from "../services/cable";
import axios from "axios";

// Import apiClient at the module level
import apiClient from "../services/apiClient";

/**
 * Helper function to ensure authorization headers are set correctly
 * Updates both the apiClient and global axios instances
 */
function updateAuthHeaders(token) {
  // Update global axios instance (used by many stores)
  if (axios && axios.defaults) {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }

  // Update apiClient's axios instance
  if (apiClient && apiClient.axios && apiClient.axios.defaults) {
    if (token) {
      apiClient.axios.defaults.headers.common["Authorization"] =
        `Bearer ${token}`;
    } else {
      delete apiClient.axios.defaults.headers.common["Authorization"];
    }
  }
}

export const useAuthStoreRefactor = defineStore("authRefactor", {
  state: () => {
    // Get token from localStorage on initialization
    const token = localStorage.getItem("token");

    // Immediately set auth headers if token exists
    if (token) {
      updateAuthHeaders(token);
    }

    return {
      // Authentication state
      token: token || null,
      user: null,
      loggedIn: !!token,

      // Loading and error states
      loading: false,
      error: null,

      // User fetching state
      lastUserFetch: null,
      userCacheTimeout: 5 * 60 * 1000, // 5 minutes cache timeout
      pendingUserRequest: null,
    };
  },

  getters: {
    /**
     * Check if user data should be refetched
     */
    shouldRefetchUser: (state) => {
      if (!state.lastUserFetch) return true;
      if (!state.user) return true;
      return Date.now() - state.lastUserFetch > state.userCacheTimeout;
    },

    /**
     * Get authentication status object (for backward compatibility)
     */
    status: (state) => ({
      loggedIn: state.loggedIn,
    }),

    /**
     * Check if user is authenticated and has user data
     */
    isAuthenticated: (state) => state.loggedIn && !!state.user,

    /**
     * Get user's display name
     */
    userDisplayName: (state) => {
      if (!state.user) return null;
      return state.user.name || state.user.email || "Unknown User";
    },
  },

  actions: {
    /**
     * Set authentication token and update state
     */
    setToken(token) {
      this.token = token;
      this.loggedIn = !!token;

      if (token) {
        localStorage.setItem("token", token);
      } else {
        localStorage.removeItem("token");
      }

      // Immediately update auth headers
      updateAuthHeaders(token);
    },

    /**
     * Clear all authentication state
     */
    clearAuth() {
      // Reset the ActionCable connection before clearing auth data
      try {
        resetAuthenticatedConsumer();
      } catch (error) {
        console.warn("AuthStore: Error resetting ActionCable:", error);
      }

      // Clear state
      this.token = null;
      this.user = null;
      this.loggedIn = false;
      this.lastUserFetch = null;
      this.pendingUserRequest = null;
      this.error = null;

      // Clear storage and API client
      this.setToken(null);
    },

    /**
     * Handle successful authentication response
     */
    handleAuthSuccess(response, isSSO = false) {
      const token = response.auth_token;

      if (!token) {
        console.error(
          `AuthStore: No token found in ${isSSO ? "SSO" : "login"} response`,
        );
        throw new Error(
          `No token found in ${isSSO ? "SSO" : "login"} response`,
        );
      }

      // Set token - user data will be fetched separately
      this.setToken(token);
      this.lastUserFetch = null; // Reset to force fresh user fetch
      this.error = null;

      // Fetch fresh user data to ensure we have the latest information
      this.getCurrentUser(true);

      return response;
    },

    /**
     * Handle authentication failure
     */
    handleAuthFailure(error) {
      console.error("AuthStore: Authentication failed:", error);
      this.clearAuth();
      this.error = error.message || "Authentication failed";
      throw error;
    },

    /**
     * Login with email and password
     */
    async login(credentials) {
      try {
        this.loading = true;
        this.error = null;

        const response = await apiClient.post("/api/v1/authenticate", {
          email: credentials.email,
          password: credentials.password,
        });

        return this.handleAuthSuccess(response, false);
      } catch (error) {
        return this.handleAuthFailure(error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Login with SSO token
     */
    async loginSSO({ token }) {
      try {
        this.loading = true;
        this.error = null;

        const response = await apiClient.post("/api/v1/authenticate_sso", "", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        return this.handleAuthSuccess(response, true);
      } catch (error) {
        console.error("AuthStore: SSO authentication failed:", error);
        return this.handleAuthFailure(error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Logout user
     */
    async logout() {
      this.clearAuth();

      // Note: MSAL logout is handled in the LogOutButton component
      // to allow for proper redirect handling
    },

    /**
     * Get current user data with caching and deduplication
     */
    async getCurrentUser(forceRefresh = false) {
      try {
        // Only make the request if we have a token
        if (!this.token) {
          console.log("AuthStore: getCurrentUser - No token available");
          return null;
        }

        // Use cached data if available and not expired
        if (!forceRefresh && !this.shouldRefetchUser && this.user) {
          console.log("AuthStore: getCurrentUser - Using cached user data");
          return this.user;
        }

        // Deduplicate in-flight requests
        if (this.pendingUserRequest) {
          console.log("AuthStore: getCurrentUser - Using pending request");
          return this.pendingUserRequest;
        }

        console.log(
          "AuthStore: getCurrentUser - Making API call to /api/v1/users/current",
        );
        this.loading = true;
        this.error = null;

        // Create and store the request promise
        this.pendingUserRequest = apiClient
          .get("/api/v1/users/current")
          .then((userData) => {
            console.log(
              "AuthStore: getCurrentUser - API response received:",
              userData,
            );
            console.log(
              "AuthStore: getCurrentUser - Active day report ID:",
              userData?.active_day_report_id,
            );

            this.user = userData;
            this.lastUserFetch = Date.now();
            this.error = null;
            return this.user;
          })
          .catch((error) => {
            console.error("AuthStore: Error fetching current user:", error);

            // Set error message (401s will be handled by apiClient + router guard)
            this.error = error.message || "Failed to fetch user data";
            throw error;
          })
          .finally(() => {
            this.loading = false;
            this.pendingUserRequest = null;
          });

        return this.pendingUserRequest;
      } catch (error) {
        this.loading = false;
        this.pendingUserRequest = null;
        throw error;
      }
    },

    /**
     * Debug method to manually check user data (for browser console)
     */
    async debugCurrentUser() {
      console.log("=== AuthStore Debug ===");
      console.log("Token exists:", !!this.token);
      console.log("Current user:", this.user);
      console.log("Last user fetch:", this.lastUserFetch);
      console.log("Should refetch:", this.shouldRefetchUser);

      if (this.token) {
        console.log("Forcing fresh user fetch...");
        try {
          const user = await this.getCurrentUser(true);
          console.log("Fresh user data:", user);
          return user;
        } catch (error) {
          console.error("Error fetching user:", error);
          return null;
        }
      } else {
        console.log("No token available");
        return null;
      }
    },

    /**
     * Initialize authentication state on app startup
     */
    initializeAuth() {
      console.log("AuthStore: initializeAuth called");
      console.log("AuthStore: Current token exists:", !!this.token);

      // Ensure headers are set if we have a token
      if (this.token) {
        updateAuthHeaders(this.token);
        console.log("AuthStore: Headers set, attempting to fetch current user");

        this.getCurrentUser()
          .then((user) => {
            console.log("AuthStore: User loaded in initializeAuth:", user);
            console.log(
              "AuthStore: User active_day_report_id:",
              user?.active_day_report_id,
            );
          })
          .catch((error) => {
            console.warn(
              "AuthStore: Failed to fetch user on initialization:",
              error,
            );
            // Don't clear auth on initialization failure - let the user try again
          });
      } else {
        console.log("AuthStore: No token found, skipping user fetch");
      }
    },
  },
});

/**
 * Set up a watcher to sync token changes with API client headers
 * This is now simplified since the main work is done in setToken
 */
export function setupAuthWatcher(pinia) {
  const authStore = useAuthStoreRefactor(pinia);

  // Watch for token changes and update headers (redundant safety)
  watch(
    () => authStore.token,
    (newToken) => {
      updateAuthHeaders(newToken);
    },
    { immediate: true },
  );

  return authStore;
}
